import { api } from './api'

export type NotificationCategory = 'deals' | 'contracts' | 'viewings' | 'tasks' | 'properties' | 'calls' | 'notes' | 'emails' | 'system' | 'monitor'

export interface Notification {
  id: string
  subject: string | null
  body: string
  eventType: string | null
  category: NotificationCategory | null
  entityType: string | null
  entityId: string | null
  metadata: Record<string, unknown>
  readAt: string | null
  createdAt: string
}

/**
 * The backend returns { data: Notification[], meta: { total } } for the list endpoint.
 * The Axios interceptor preserves this envelope intact (it only strips { data } when there
 * is no `meta` key). So r.data here is the full { data, meta } object.
 */
export interface NotificationListResponse {
  data: Notification[]
  meta: { total: number }
}

export interface PreferenceCategory {
  key: NotificationCategory
  label: string
  icon: string
  enabled: boolean
  events: Array<{
    key: string
    label: string
    defaultEnabled: boolean
    enabled: boolean
  }>
}

export interface EventCatalogResponse {
  categories: Array<{
    key: NotificationCategory
    label: string
    icon: string
    events: Array<{ key: string; label: string; defaultEnabled: boolean }>
  }>
}

// Conventions (matches all other admin API callers):
//   api.get<T>(...) returns AxiosResponse where res.data is T (after interceptor unwrap)
//   callers use .then((r) => r.data) to get T, or const { data } = await api.get<T>(...)
//
// For the list endpoint the backend returns { data: Notification[], meta: { total } }.
// The interceptor detects `meta` and preserves the envelope — so r.data is NotificationListResponse.

export const notificationsApi = {
  list: (params: { unread?: boolean; category?: string; limit?: number; offset?: number } = {}) =>
    api.get<NotificationListResponse>('/notifications', { params }).then((r) => r.data),

  unreadCount: () =>
    api.get<{ count: number }>('/notifications/unread-count').then((r) => r.data.count),

  markAsRead: (id: string) =>
    api.patch(`/notifications/${id}/read`).then((r) => r.data),

  markAllAsRead: () =>
    api.patch('/notifications/read-all').then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/notifications/${id}`).then((r) => r.data),

  bulkAction: (ids: string[], action: 'read' | 'delete') =>
    api.post('/notifications/bulk-action', { ids, action }).then((r) => r.data),

  getPreferences: () =>
    api.get<PreferenceCategory[]>('/notifications/preferences').then((r) => r.data),

  updatePreference: (channel: string, enabled: boolean) =>
    api.patch('/notifications/preferences', { channel, enabled }).then((r) => r.data),

  getEventCatalog: () =>
    api.get<EventCatalogResponse>('/notifications/event-catalog').then((r) => r.data),
}
